import * as db from '@/data';
import { expiryLabel, normalizeUnit } from '@/lib/invite';
import type {
  Certificate,
  Course,
  Enrollment,
  Invite,
  OrgNode,
  Profile,
  Question,
  QuizQuestion,
  Role,
  RoleDef,
  RosterEntry,
  TestDraftRecord,
  UserStatus,
} from '@/types/domain';
import type { CertificateVerification, CreateInviteInput, DataRepository } from './types';
import { eligibleForCert, issuanceMode } from '@/lib/requirements';

/** Resolve after a short, jittered delay so loading/empty states are exercised. */
function delay<T>(value: T, ms = 220): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms + Math.random() * 180));
}

/** Deep clone so callers can never mutate the in-memory seed by reference. */
function clone<T>(value: T): T {
  return typeof structuredClone === 'function'
    ? structuredClone(value)
    : (JSON.parse(JSON.stringify(value)) as T);
}

/** Strip the answer key — learners never receive it. */
function stripAnswer(q: Question): QuizQuestion {
  return { id: q.id, type: q.type, stem: q.stem, imageUrl: q.imageUrl, options: q.options, points: 1 };
}
function answerCorrect(q: Question, a: number | number[] | undefined): boolean {
  if (a == null) return false;
  if (Array.isArray(q.answer)) {
    const got = Array.isArray(a) ? a : [a];
    return got.length === q.answer.length && q.answer.every((x) => got.includes(x));
  }
  return a === q.answer;
}

/* Mutable in-memory copies — the mock "database". */
const _users: Profile[] = clone(db.users);
const _invites: Invite[] = clone(db.invites);
const _enrollments: Enrollment[] = clone(Object.values(db.myEnrollments));
const _courses: Course[] = clone(db.courses);
const _roster: RosterEntry[] = clone(db.roster);
const _certificates: Certificate[] = clone(db.certificates);
let _orgTree: OrgNode[] = clone(db.orgTree);
const mockDrafts = new Map<string, TestDraftRecord>();

/** Build a fresh certificate for a completed course. Instructor name is a
 *  placeholder until the real names are provided (then seed them per course). */
function makeCert(course: Course): Certificate {
  const now = new Date();
  const exp = new Date(now);
  exp.setFullYear(exp.getFullYear() + course.certYears);
  const fmt = (d: Date) => d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  return {
    id: `${course.code}-${now.getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`,
    course: course.title.en,
    th: course.title.th,
    host: course.host,
    issued: fmt(now),
    expires: fmt(exp),
    status: 'valid',
    instructor: course.host,
  };
}
const existingCert = (course: Course) => _certificates.find((c) => c.course === course.title.en);

/** Auto-issue a certificate once all (non-approval) requirements are met. */
function autoIssue(courseId: string) {
  const c = _courses.find((x) => x.id === courseId);
  const e = _enrollments.find((x) => x.courseId === courseId);
  if (!c || !e || issuanceMode(c) !== 'auto') return;
  if (!eligibleForCert(c, e) || existingCert(c)) return;
  _certificates.unshift(makeCert(c));
  e.status = 'completed';
}

function markLessonDone(lessonId: string) {
  for (const c of _courses) {
    const l = c.lessons.find((x) => x.id === lessonId);
    if (!l) continue;

    l.done = true;
    const enr = _enrollments.find((e) => e.courseId === c.id);
    if (enr) {
      const done = c.lessons.filter((x) => x.done).length;
      enr.progress = c.lessons.length ? Math.round((done / c.lessons.length) * 100) : 100;
      if (enr.status === 'enrolled') enr.status = 'in-progress';
    }
    autoIssue(c.id);
    break;
  }
}

export const mockRepository: DataRepository = {
  /* Catalog */
  listCourses: () => delay(clone(_courses)),
  getCourse: (id) => delay(clone(_courses.find((c) => c.id === id) ?? null)),
  updateCourse: (id, patch) => {
    const c = _courses.find((x) => x.id === id);
    if (!c) throw new Error('Course not found');
    Object.assign(c, patch);
    return delay(clone(c));
  },
  updateLesson: (courseId, lessonId, patch) => {
    const c = _courses.find((x) => x.id === courseId);
    const l = c?.lessons.find((x) => x.id === lessonId);
    if (!c || !l) throw new Error('Lesson not found');
    Object.assign(l, patch);
    return delay(undefined);
  },

  /* Learner */
  getMe: () => delay(clone(db.me)),
  completeProfile: () => delay(undefined),
  listMyEnrollments: () => delay(clone(_enrollments)),
  enroll: (courseId) => {
    let row = _enrollments.find((e) => e.courseId === courseId);
    if (!row) {
      row = { courseId, progress: 0, attended: null, pretest: null, posttest: null, eval: null, status: 'enrolled' };
      _enrollments.push(row);
    }
    return delay(clone(row));
  },
  submitEvaluation: (courseId) => {
    const enr = _enrollments.find((e) => e.courseId === courseId);
    if (enr) {
      enr.eval = true;
      if (enr.status === 'enrolled') enr.status = 'in-progress';
    }
    autoIssue(courseId);
    return delay(undefined);
  },

  /* Assessment */
  getTestQuestions: (courseId, kind) =>
    delay(db.questions.filter((q) => q.courseId === courseId && (!kind || q.testKind === kind)).map(stripAnswer)),
  getTestReview: (_courseId, kind) => delay({ kind, score: 0, questions: [] }),
  getSurveySummary: (_courseId) => {
    // Mock survey responses so the analytics + dean report preview look real.
    const ids = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7'];
    const n = 15;
    const answers = Array.from({ length: n }, (_, i) =>
      Object.fromEntries(ids.map((q, j) => [q, 4 + ((i + j) % 2)])) as Record<string, number>,
    );
    const comments = [
      'อบรมดีมากค่ะ ได้ความรู้เยอะ วิทยากรอธิบายเข้าใจง่าย',
      'อยากให้มีการฝึกปฏิบัติ (hands-on) เพิ่มอีกหน่อย',
      'เนื้อหาแน่นมาก นำไปใช้ในการทำงานจริงได้เลย',
      'สถานการณ์จำลองช่วยให้เข้าใจขั้นตอน ACLS มากขึ้น',
      'เวลาค่อนข้างกระชั้น แต่โดยรวมพึงพอใจมากครับ',
      'ขอบคุณทีม ER ที่จัดอบรมดี ๆ แบบนี้',
    ];
    return delay({ count: n, answers, comments });
  },
  getItemAnalysis: (courseId, kind) => {
    // Simulated cohort: deterministic, varied "% wrong" so the most-missed deck
    // and the analytics panel look believable in mock mode.
    const total = kind === 'post' ? 50 : 54;
    const pool = db.questions.filter((q) => q.courseId === courseId && q.testKind === kind);
    const items = pool.map((q, i) => {
      const pct = ((i * 17 + 7) % 70) + 5; // 5..74
      const wrong = Math.round((pct / 100) * total);
      return { id: q.id, ord: i + 1, stem: q.stem, wrong, total, wrongPct: Math.round((wrong / total) * 100) };
    });
    items.sort((a, b) => b.wrong - a.wrong || a.ord - b.ord);
    return delay(items);
  },
  submitTest: (courseId, kind, answers) => {
    const pool = db.questions.filter((q) => q.courseId === courseId && q.testKind === kind);
    const course = _courses.find((c) => c.id === courseId);
    const pass = course?.passScore ?? 80;
    const total = pool.length;
    const correct = pool.filter((q) => answerCorrect(q, answers[q.id])).length;
    const score = total ? Math.round((correct / total) * 100) : 0;
    const reveal = kind === 'pre' && course?.pretestRevealAnswers === true;
    const key = reveal ? Object.fromEntries(pool.map((q) => [q.id, q.answer])) : null;
    const enr = _enrollments.find((e) => e.courseId === courseId);
    if (enr) {
      if (kind === 'pre') enr.pretest = score;
      else enr.posttest = score;
      if (enr.status === 'enrolled') enr.status = 'in-progress';
    }
    autoIssue(courseId);
    mockDrafts.delete(`${courseId}:${kind}`);
    return delay({ score, passScore: pass, passed: score >= pass, reveal, key });
  },
  getTestDraft: (courseId, kind) => delay(mockDrafts.get(`${courseId}:${kind}`) ?? null),
  saveTestDraft: (courseId, kind, draft) => { mockDrafts.set(`${courseId}:${kind}`, draft); return delay(undefined); },
  clearTestDraft: (courseId, kind) => { mockDrafts.delete(`${courseId}:${kind}`); return delay(undefined); },
  getLessonQuiz: (lessonId) =>
    delay(db.questions.filter((q) => q.lessonId === lessonId).map((q) => ({ ...stripAnswer(q), answer: q.answer }))),
  submitLessonQuiz: (lessonId, answers) => {
    const qs = db.questions.filter((q) => q.lessonId === lessonId);
    const correct = qs.filter((q) => answerCorrect(q, answers[q.id])).length;
    const key: Record<string, number | number[]> = {};
    qs.forEach((q) => {
      key[q.id] = q.answer;
    });
    markLessonDone(lessonId);
    return delay({ score: qs.length ? Math.round((correct / qs.length) * 100) : 100, correct, total: qs.length, reveal: true, key });
  },
  completeLesson: (lessonId) => {
    markLessonDone(lessonId);
    return delay(undefined);
  },

  /* Certificates */
  listCertificates: () => delay(clone(_certificates)),
  issueCertificate: (courseId) => {
    const c = _courses.find((x) => x.id === courseId);
    if (!c) return delay(null);
    let cert = existingCert(c);
    if (!cert) {
      cert = makeCert(c);
      _certificates.unshift(cert);
      const e = _enrollments.find((x) => x.courseId === courseId);
      if (e) e.status = 'completed';
    }
    return delay(clone(cert));
  },
  verifyCertificate: (code) => {
    const certificate = db.certificates.find((c) => c.id.toLowerCase() === code.trim().toLowerCase());
    if (!certificate) return delay<CertificateVerification>({ result: 'not-found' });
    const result =
      certificate.status === 'revoked' ? 'revoked' : certificate.status === 'expired' ? 'expired' : 'valid';
    return delay<CertificateVerification>({ result, certificate: clone(certificate) });
  },

  /* Instructor */
  listRoster: () => delay<RosterEntry[]>(clone(_roster)),
  markAttendance: (courseId, userId) => {
    if (userId) {
      const r = _roster.find((x) => x.emp === userId);
      if (r) r.attended = true;
    } else {
      const enr = _enrollments.find((e) => e.courseId === courseId);
      if (enr) {
        enr.attended = true;
        if (enr.status === 'enrolled') enr.status = 'in-progress';
      }
      autoIssue(courseId);
    }
    return delay(undefined);
  },
  setCheckin: (_courseId, open) => delay({ open, token: 'MOCK-CHECKIN' }),
  checkIn: (courseId) => {
    const enr = _enrollments.find((e) => e.courseId === courseId);
    if (enr) { enr.attended = true; if (enr.status === 'enrolled') enr.status = 'in-progress'; autoIssue(courseId); }
    return delay(undefined);
  },
  markAttended: (_courseId, userId) => {
    const r = _roster.find((x) => x.userId === userId || x.emp === userId);
    if (r) r.attended = true;
    return delay(undefined);
  },
  setAttended: (_courseId, userId, present) => {
    const r = _roster.find((x) => x.userId === userId || x.emp === userId);
    if (r) r.attended = present;
    return delay(undefined);
  },
  listQuestions: (bankId) => {
    // Mock shim: the real PRE/POST banks are keyed by uuid, but mock questions
    // tag their test via `testKind`. Resolve those two ids by testKind (mirrors
    // what prod returns); otherwise fall back to matching the bank name.
    const PRE = 'a0000000-0000-4000-8000-000000000001';
    const POST = 'c0000000-0000-4000-8000-000000000003';
    const list =
      bankId === PRE ? db.questions.filter((q) => q.testKind === 'pre')
        : bankId === POST ? db.questions.filter((q) => q.testKind === 'post')
          : bankId ? db.questions.filter((q) => q.bank === bankId)
            : db.questions;
    return delay<Question[]>(clone(list));
  },

  /* Admin */
  listInvites: () => delay(clone(_invites)),
  createInvite: (input: CreateInviteInput) => {
    const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
    const unit = normalizeUnit(input.unit);
    const invite: Invite = {
      id: `inv-${Date.now()}`,
      token: `ED-${rand}-${input.profession.slice(0, 3)}-${(unit ?? 'ORG').replace(/\s+/g, '').slice(0, 3).toUpperCase()}`,
      role: input.role,
      profession: input.profession,
      dept: input.dept,
      unit: unit ?? '',
      maxUses: input.maxUses,
      uses: 0,
      expiresAt: expiryLabel(input.expiresAt),
      status: 'active',
      createdBy: db.me.firstEn,
      createdAt: 'just now',
    };
    _invites.unshift(invite);
    return delay(clone(invite));
  },
  revokeInvite: (id) => {
    const inv = _invites.find((x) => x.id === id);
    if (inv) inv.status = 'revoked';
    return delay(undefined);
  },
  listUsers: () => delay(clone(_users)),
  updateUserRole: (id, role: Role) => {
    const u = _users.find((x) => x.id === id);
    if (!u) throw new Error(`Unknown user ${id}`);
    u.role = role;
    return delay(clone(u));
  },
  setUserStatus: (id, status: UserStatus) => {
    const u = _users.find((x) => x.id === id);
    if (!u) throw new Error(`Unknown user ${id}`);
    u.status = status;
    return delay(clone(u));
  },
  updateMyProfile: (patch) => {
    Object.assign(db.me, {
      firstEn: patch.firstEn, lastEn: patch.lastEn, firstTh: patch.firstTh, lastTh: patch.lastTh,
      ...(patch.tone ? { tone: patch.tone } : {}),
    });
    return delay(clone(db.me));
  },
  adminUpdateProfile: (id, patch) => {
    const u = _users.find((x) => x.id === id);
    if (!u) throw new Error(`Unknown user ${id}`);
    Object.assign(u, {
      firstEn: patch.firstEn, lastEn: patch.lastEn, firstTh: patch.firstTh, lastTh: patch.lastTh,
      ...(patch.tone ? { tone: patch.tone } : {}),
      ...(patch.profession !== undefined ? { profession: patch.profession } : {}),
      ...(patch.deptId !== undefined ? { deptId: patch.deptId } : {}),
      ...(patch.unitId !== undefined ? { unitId: patch.unitId } : {}),
      ...(patch.email !== undefined ? { email: patch.email } : {}),
      ...(patch.titlePrefix !== undefined ? { titlePrefix: patch.titlePrefix } : {}),
      ...(patch.titleSuffix !== undefined ? { titleSuffix: patch.titleSuffix } : {}),
    });
    return delay(clone(u));
  },
  listOrg: () => delay<OrgNode[]>(clone(_orgTree)),
  createDept: (input) => {
    _orgTree.push({ deptId: crypto.randomUUID().slice(0, 8), dept: input.nameEn, th: input.nameTh, units: [], staff: 0 });
    return delay<void>(undefined);
  },
  updateDept: (id, patch) => {
    const d = _orgTree.find((x) => x.deptId === id);
    if (d) {
      if (patch.nameEn !== undefined) d.dept = patch.nameEn;
      if (patch.nameTh !== undefined) d.th = patch.nameTh;
    }
    return delay<void>(undefined);
  },
  deleteDept: (id) => {
    _orgTree = _orgTree.filter((x) => x.deptId !== id);
    return delay<void>(undefined);
  },
  createUnit: (input) => {
    const d = _orgTree.find((x) => x.deptId === input.deptId);
    if (d) d.units.push({ id: crypto.randomUUID().slice(0, 8), name: input.name });
    return delay<void>(undefined);
  },
  updateUnit: (id, name) => {
    for (const d of _orgTree) {
      const u = d.units.find((x) => x.id === id);
      if (u) u.name = name;
    }
    return delay<void>(undefined);
  },
  deleteUnit: (id) => {
    for (const d of _orgTree) d.units = d.units.filter((x) => x.id !== id);
    return delay<void>(undefined);
  },
  getRoleDefs: () => delay<Record<Role, RoleDef>>(clone(db.roleDefs)),
};

export type { Course, Certificate };
